import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, FileText, Check } from 'lucide-react';
import Layout from '../components/Layout';
import { fetchProjects } from '../lib/cms';
import Lightbox from '../components/Lightbox';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProjectIndex, setActiveProjectIndex] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [highlightedProjectId, setHighlightedProjectId] = useState(null);

  useEffect(() => {
    fetchProjects().then(data => {
      setProjects(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!loading) {
      let highlightTimer;

      const handleHashScroll = () => {
        const hash = window.location.hash;
        if (hash) {
          const id = hash.substring(1);
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlightedProjectId(id);

            if (highlightTimer) clearTimeout(highlightTimer);
            highlightTimer = setTimeout(() => {
              setHighlightedProjectId(null);
            }, 2500); // Highlight for 2.5 seconds
          }
        }
      };

      // Run after a short delay to ensure the DOM is fully rendered and painted
      const timer = setTimeout(handleHashScroll, 150);

      window.addEventListener('hashchange', handleHashScroll);
      return () => {
        clearTimeout(timer);
        if (highlightTimer) clearTimeout(highlightTimer);
        window.removeEventListener('hashchange', handleHashScroll);
      };
    }
  }, [loading]);

  if (loading) {
    return (
      <Layout>
        <div className="py-24 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500 font-bold uppercase tracking-widest">Henter projekter...</p>
        </div>
      </Layout>
    );
  }

  const openLightbox = (projectIndex) => {
    setActiveProjectIndex(projectIndex);
    setActiveImageIndex(0);
  };

  const closeLightbox = () => {
    setActiveProjectIndex(null);
    setActiveImageIndex(0);
  };

  const nextImage = () => {
    const project = projects[activeProjectIndex];
    if (!project) return;
    const images = Array.isArray(project.image) ? project.image : [project.image];
    setActiveImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const project = projects[activeProjectIndex];
    if (!project) return;
    const images = Array.isArray(project.image) ? project.image : [project.image];
    setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const activeProject = activeProjectIndex !== null ? projects[activeProjectIndex] : null;
  const activeProjectImages = activeProject 
    ? (Array.isArray(activeProject.image) ? activeProject.image : [activeProject.image])
    : [];

  // Struktureret data for projekter (ItemList)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "CM Skruefundament Projekter",
    "description": "Et udvalg af vores seneste projekter med skruefundamenter i hele Danmark.",
    "itemListElement": projects.map((project, index) => {
      const title = project.title?.da || project.title || 'Projekt';
      return {
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://cmskruefundament.dk/projekter#project-${project.id}`,
        "name": typeof title === 'string' ? title : 'Projekt'
      };
    })
  };

  return (
    <Layout>
      <Helmet>
        <title>Projekter & Cases | CM Skruefundament ApS</title>
        <meta name="description" content="Se vores udvalgte projekter med skruefundamenter. Fra Aarhus Street Food til private terrasser og erhvervsbyggeri." />
        <link rel="canonical" href="https://cmskruefundament.dk/projekter" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cmskruefundament.dk/projekter" />
        <meta property="og:title" content="Projekter & Cases | CM Skruefundament ApS" />
        <meta property="og:description" content="Se vores udvalgte projekter med skruefundamenter. Fra Aarhus Street Food til private terrasser og erhvervsbyggeri." />
        {projects.length > 0 && (
          <meta property="og:image" content={Array.isArray(projects[0].image) ? projects[0].image[0] : projects[0].image} />
        )}
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="CM Skruefundament ApS" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cmskruefundament.dk/projekter" />
        <meta name="twitter:title" content="Projekter & Cases | CM Skruefundament ApS" />
        <meta name="twitter:description" content="Se vores udvalgte projekter med skruefundamenter. Fra Aarhus Street Food til private terrasser og erhvervsbyggeri." />
        {projects.length > 0 && (
          <meta name="twitter:image" content={Array.isArray(projects[0].image) ? projects[0].image[0] : projects[0].image} />
        )}

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="bg-slate-900 py-16 md:py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <div className="lg:col-span-7">
              <span className="text-xs font-bold uppercase tracking-[0.3em] mb-6 block text-amber-400">Cases & Referencer</span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase leading-tight tracking-tight mb-6">
                Vores Projekter
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-light mb-8">
                Hos CM Skruefundament har vi udført skruefundering til alt fra små private terrasser og carporte til store modulbyggerier og erhvervsløsninger som Aarhus Street Food. 
                Gå på opdagelse i vores projekter herunder.
              </p>

              <div className="flex flex-wrap gap-3 text-xs uppercase tracking-wider font-semibold text-slate-400">
                <span className="bg-slate-800 px-3.5 py-1.5 rounded-full border border-slate-700 text-slate-200">Aarhus Street Food</span>
                <span className="bg-slate-800 px-3.5 py-1.5 rounded-full border border-slate-700 text-slate-200">Træterrasser</span>
                <span className="bg-slate-800 px-3.5 py-1.5 rounded-full border border-slate-700 text-slate-200">Sommerhuse</span>
                <span className="bg-slate-800 px-3.5 py-1.5 rounded-full border border-slate-700 text-slate-200">Erhvervsbyggeri</span>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-5" id="kontakt-form">
              <ContactForm
                variant="hero"
                defaultService="Skruefundering generelt"
                title="Har du et lignende projekt?"
                subtitle="Få en uforpligtende vurdering og et tilbud på skruefundament til dit næste byggeprojekt."
              />
            </div>

          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {projects && projects.length > 0 ? projects.map((project, index) => {
              const title = project.title?.da || project.title || 'Uden titel';
              const category = project.category?.da || project.category || 'Projekt';
              const description = project.description?.da || project.description || '';
              const imageUrl = Array.isArray(project.image) ? project.image[0] : project.image;
              const isHighlighted = `project-${project.id}` === highlightedProjectId;

              return (
                <div 
                  key={project.id || index} 
                  id={`project-${project.id}`} 
                  className={`group scroll-mt-32 transition-all duration-1000 rounded-2xl p-6 -m-6 ${
                    isHighlighted 
                      ? 'bg-slate-50 ring-2 ring-slate-900/10 shadow-xl scale-[1.02] z-10 relative' 
                      : 'bg-transparent ring-0 ring-transparent shadow-none scale-100 z-0 relative'
                  }`}
                >
                  <figure 
                    className="aspect-video overflow-hidden mb-8 cursor-pointer relative m-0 rounded-lg group/fig"
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={getOptimizedImageUrl(imageUrl, { width: 800, quality: 80 })}
                      alt={`Referenceprojekt: ${typeof title === 'string' ? title : 'Projekt'} – ${typeof category === 'string' ? category : 'Skruefundament'} udført af CM Skruefundament`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-transparent transition-colors"></div>
                    <div className="absolute top-4 left-4 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-2 z-10">
                      {typeof category === 'string' ? category : 'Projekt'}
                    </div>
                    {Array.isArray(project.image) && project.image.length > 1 && (
                      <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-xs text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm z-10">
                        {project.image.length} billeder
                      </div>
                    )}
                    <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-950/80 via-slate-950/40 to-transparent p-3 text-white text-xs opacity-90 truncate">
                      {typeof title === 'string' ? title : 'Reference'} – Skruefundering i praksis
                    </figcaption>
                  </figure>
                  <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-4">
                    {typeof title === 'string' ? title : 'Projekt'}
                  </h2>
                  <div 
                    className="text-slate-600 mb-6 leading-relaxed prose prose-slate max-w-none"
                    dangerouslySetInnerHTML={{ __html: typeof description === 'string' ? description : '' }}
                  />
                  <button
                    onClick={() => openLightbox(index)}
                    className="text-xs font-bold uppercase tracking-widest border-b-2 border-slate-900 pb-1 hover:text-slate-500 hover:border-slate-500 transition-colors"
                  >
                    Se billede i stort
                  </button>
                </div>
              );
            }) : (
              <div className="col-span-2 text-center py-12 border-2 border-dashed border-slate-200">
                <p className="text-slate-500">Ingen projekter fundet.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={activeProjectImages}
        currentIndex={activeProjectIndex !== null ? activeImageIndex : null}
        projectTitle={activeProjectIndex !== null && projects[activeProjectIndex] ? (projects[activeProjectIndex].title?.da || projects[activeProjectIndex].title) : undefined}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />

      {/* Inquiry & Contact Form Section */}
      <section id="tilbud" className="bg-slate-900 py-20 lg:py-24 text-white border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-amber-500 mb-4 block">Få et tilbud</span>
              <h2 className="text-[1.4rem] md:text-[1.6rem] min-[992px]:text-[2.4rem] font-bold uppercase tracking-tight mb-6 text-white leading-tight">
                Har du et lignende projekt i tankerne?
              </h2>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-8">
                Uanset om du planlægger en træterrasse, et sommerhus, modulbyggeri, solceller eller industriel forankring, kan vi dimensionere og montere det perfekte skruefundament.
              </p>

              <div className="space-y-4 mb-8">
                <a
                  href="tel:26796139"
                  className="flex items-center gap-4 p-4 bg-slate-800/90 rounded-lg border border-slate-700 hover:border-amber-500 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Direkte kontakt</p>
                    <p className="text-lg font-bold text-white">26 79 61 39</p>
                  </div>
                </a>

                <a
                  href="mailto:cs@cmskruefundament.dk"
                  className="flex items-center gap-4 p-4 bg-slate-800/90 rounded-lg border border-slate-700 hover:border-amber-500 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-400">Send projektmateriale til</p>
                    <p className="text-base font-bold text-white">cs@cmskruefundament.dk</p>
                  </div>
                </a>
              </div>

              <div className="border-t border-slate-800 pt-6">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Vedhæft dit projektmateriale</h4>
                <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                  Du kan uploade situationsplaner, tegninger, jordbundsrapporter eller skitser direkte i formularen, så beregner vi den præcise løsning til dig.
                </p>
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-400">
                  <Check className="w-4 h-4" />
                  <span>Svar indenfor 24 timer på hverdage</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
